/**
 * New Customers page — where the dashboard's "New Customer Information →
 * View all" lands.
 *
 * A port of the Command Center's New Customer Info page rather than a jump
 * into the Customers directory: the latest signups as profile cards, each
 * reading top-down from "where did this person come from" to "how far into
 * signup did they get". Same endpoint as the dashboard cards
 * (`admin/users/customers/recent-signups`), which merges each signup with the
 * profile the signup-profile worker builds about an hour after signup.
 *
 * @module pages/NewCustomers
 */

import React, { useMemo, useState } from 'react';
import { Box, InputBase, Skeleton, Stack, Typography } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { Title } from 'react-admin';
import { useNavigate } from 'react-router-dom';

import { useAdminTheme } from '../admin-theme';
import { FONT_DATA, FONT_DISPLAY } from '../theme/grounds';
import { useRecentSignups, ago, type RecentSignup } from './components/NewCustomerCards';

type Colors = ReturnType<typeof useAdminTheme>['colors'];

function initials(name: string | null | undefined): string {
  const parts = (name ?? '').split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

const yn = (b: boolean) => (b ? 'Yes' : 'No');

/**
 * One attribute line: emoji · label · value. `kind` colours the value —
 * yes/no for the checks, muted italics for an unresolved field.
 */
function Attr({
  icon,
  label,
  value,
  kind,
  colors,
}: {
  icon: string;
  label: string;
  value: React.ReactNode;
  kind?: 'yes' | 'no' | 'grey' | 'muted';
  colors: Colors;
}): React.ReactElement {
  const valueColor =
    kind === 'yes' ? colors.success : kind === 'no' ? colors.error : kind ? colors.textMuted : colors.textPrimary;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '9px',
        py: '7px',
        borderTop: `1px solid ${colors.border}`,
        fontSize: '0.84rem',
        '&:first-of-type': { borderTop: 'none' },
      }}
    >
      {/* Emoji glyphs — a fixed box so the labels line up despite the varying
          advance widths of each emoji. */}
      <Box component="span" sx={{ flexShrink: 0, display: 'inline-flex', justifyContent: 'center', width: 16, fontSize: 13, lineHeight: 1 }}>
        {icon}
      </Box>
      <Box component="span" sx={{ color: colors.textMuted, flexShrink: 0, minWidth: 104 }}>
        {label}
      </Box>
      <Box
        component="span"
        title={typeof value === 'string' ? value : undefined}
        sx={{
          ml: 'auto',
          textAlign: 'right',
          fontWeight: kind === 'muted' ? 500 : 650,
          fontStyle: kind === 'muted' ? 'italic' : 'normal',
          color: valueColor,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

/** Secondary line under an attribute row — the resolved place, the session note. */
function SubLine({ children, colors }: { children: string; colors: Colors }): React.ReactElement {
  return (
    <Typography
      title={children}
      sx={{ textAlign: 'right', fontSize: '0.76rem', color: colors.textMuted, pb: '6px', mt: '-3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {children}
    </Typography>
  );
}

function SignupCard({ c, colors }: { c: RecentSignup; colors: Colors }): React.ReactElement {
  const pending = c.profileStatus === 'pending';
  const unknown = pending ? 'still resolving' : 'Unknown';
  const source = c.source || (c.likelySource ? `${c.likelySource} (likely)` : null);
  return (
    <Box
      sx={{
        bgcolor: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: '10px',
        p: '16px 18px',
        transition: 'border-color 0.16s ease, transform 0.16s ease',
        '&:hover': { borderColor: 'var(--ember)', transform: 'translateY(-1px)' },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: '14px' }}>
        <Box
          sx={{
            flexShrink: 0,
            width: 44,
            height: 44,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            fontFamily: FONT_DISPLAY,
            fontWeight: 800,
            fontSize: '0.95rem',
            color: 'var(--ember)',
            bgcolor: 'var(--ember-bg)',
            border: '1px solid var(--ember-line)',
          }}
        >
          {initials(c.name)}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 750, fontSize: '1.05rem', lineHeight: 1.2, color: colors.textPrimary }}>{c.name || 'Unknown'}</Typography>
          {c.email && (
            <Typography sx={{ fontSize: '0.76rem', color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {c.email}
            </Typography>
          )}
        </Box>
        <Typography sx={{ flexShrink: 0, alignSelf: 'flex-start', fontFamily: FONT_DATA, fontSize: '0.74rem', color: colors.textMuted, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          {ago(c.createdAt)}
        </Typography>
      </Stack>

      {/* Row order is deliberate: Source (+ its Airbridge detail), Location,
          Geo & Network, Platform, Device, then the three Yes/No checks. Sub-detail
          stays welded to the row it explains. */}
      <Box>
        <Attr icon="🧭" label="Source" value={source ?? unknown} kind={source ? undefined : 'muted'} colors={colors} />
        {c.sourcePage && <Attr icon="🔗" label="From page" value={c.sourcePage} colors={colors} />}
        {c.searchTerm && <Attr icon="🔍" label="Search term" value={`“${c.searchTerm}”`} colors={colors} />}
        <Attr icon="🏠" label="Location" value={c.location ?? unknown} kind={c.location ? undefined : 'muted'} colors={colors} />
        <Attr icon="📍" label="Geo & Network" value={c.geoNetwork ?? unknown} kind={c.geoNetwork ? undefined : 'muted'} colors={colors} />
        <Attr icon="💻" label="Platform" value={c.platform ?? unknown} kind={c.platform ? undefined : 'muted'} colors={colors} />
        <Attr icon="📱" label="Device" value={c.device ?? unknown} kind={c.device ? undefined : 'muted'} colors={colors} />
        <Attr icon="✅" label="Phone verified" value={yn(c.phoneVerified)} kind={c.phoneVerified ? 'grey' : 'no'} colors={colors} />
        <Attr icon="📮" label="Added address" value={yn(c.addressAdded)} kind={c.addressAdded ? 'yes' : 'grey'} colors={colors} />
        {c.addressAdded && c.firstAddress && <SubLine colors={colors}>{c.firstAddress}</SubLine>}
        <Attr icon="🔁" label="Prior customer" value={yn(c.priorCustomer)} kind={c.priorCustomer ? 'yes' : 'grey'} colors={colors} />
        {/* The session narrative closes the card — a paragraph, not a field. */}
        {c.inSession && <SubLine colors={colors}>{c.inSession}</SubLine>}
      </Box>
    </Box>
  );
}

const EmptyBox = ({ children, colors }: { children: React.ReactNode; colors: Colors }): React.ReactElement => (
  <Box sx={{ textAlign: 'center', color: colors.textMuted, p: '48px 20px', bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: '10px' }}>
    {children}
  </Box>
);

const GRID_SX = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2 } as const;

const NewCustomers = (): React.ReactElement => {
  const { colors, glassHeaderStyle } = useAdminTheme();
  const navigate = useNavigate();
  const { data, loading, error } = useRecentSignups();
  const [search, setSearch] = useState('');

  const signups = useMemo(() => data?.signups ?? [], [data]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return signups;
    return signups.filter(
      (c) =>
        (c.name ?? '').toLowerCase().includes(q) ||
        (c.email ?? '').toLowerCase().includes(q) ||
        (c.location ?? '').toLowerCase().includes(q),
    );
  }, [signups, search]);

  const pending = signups.filter((s) => s.profileStatus === 'pending').length;

  return (
    <Box sx={{ bgcolor: colors.bgDeepest, minHeight: '100vh' }}>
      <Title title="New Customers" />

      <Box sx={{ ...glassHeaderStyle, px: 2.75, py: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Box>
            <Typography
              component="h1"
              sx={{ fontFamily: FONT_DISPLAY, fontStretch: '118%', fontWeight: 800, fontSize: '1.5rem', letterSpacing: '-0.02em', lineHeight: 1, color: colors.textPrimary, m: 0 }}
            >
              New Customer Info
            </Typography>
            <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.75rem', color: colors.textMuted, mt: '6px' }}>
              Deeliver · latest {signups.length || '…'} app signups
              {pending > 0 ? ` · ${pending} profile${pending === 1 ? '' : 's'} still building` : ''}
            </Typography>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => navigate('/')}
            sx={{
              fontFamily: FONT_DATA,
              fontSize: '0.75rem',
              fontWeight: 700,
              color: colors.textSecondary,
              bgcolor: 'transparent',
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              px: '12px',
              py: '7px',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'var(--hover)', color: colors.textPrimary },
            }}
          >
            ← Dashboard
          </Box>
        </Stack>
      </Box>

      <Box sx={{ px: 2.75, py: 2.5, pb: 8 }}>
        <Box
          sx={{
            position: 'relative',
            maxWidth: 420,
            mb: 2,
            '& > svg': { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 18, color: colors.textMuted, pointerEvents: 'none' },
          }}
        >
          <SearchIcon />
          <InputBase
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, or location…"
            fullWidth
            sx={{
              bgcolor: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.textPrimary,
              fontSize: '0.9rem',
              '& input': { p: '10px 12px 10px 36px' },
              '&.Mui-focused': { borderColor: 'var(--ember)' },
            }}
          />
        </Box>

        {loading ? (
          <Box sx={GRID_SX}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={380} sx={{ bgcolor: colors.bgCard, borderRadius: '10px' }} />
            ))}
          </Box>
        ) : error || signups.length === 0 ? (
          <EmptyBox colors={colors}>{error ?? 'No signups yet.'}</EmptyBox>
        ) : rows.length === 0 ? (
          <EmptyBox colors={colors}>No customers match “{search}”.</EmptyBox>
        ) : (
          <Box sx={GRID_SX}>
            {rows.map((c) => (
              <SignupCard key={c.id} c={c} colors={colors} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NewCustomers;
